<template>
  <div class="sidebar-content sidebar-content--jacket">
    <h1 class="sidebar-title">{{ sidebarTitle }}</h1>

    <b-modal
      id="patch-editor-modal"
      size="xl"
      hide-header
      hide-footer
      centered
      content-class="patch-editor-modal__content"
    >
      <PatchEditor
        modal-id="patch-editor-modal"
        :is-pending="svgPatchPending"
        :patch-svg-text="patchSvgText"
        :patchName="patchName"
        :patchCategoryId="patchCategoryId"
        :original-patch-id="originalPatchId"
        :size="size"
        :availableSizes="availableSizes"
      />
    </b-modal>

    <div class="categories">
      <div v-if="isCategoriesPending" class="loader">
        <b-spinner label="Loading..." variant="primary"/>
      </div>

      <!-- Show Categories -->

      <div v-if="!activeCategoryId">
        <div
          v-for="categoryId in categories.ids"
          :key="categoryId"
          class="category"
          v-on:click="selectCategory(categoryId)"
        >
          <div class="category__name">{{categories.data[categoryId].name}}</div>
        </div>
      </div>

      <!-- Show subcategories for category -->

      <div
        v-if="activeCategoryId &&
        !activeSubCategoryId &&
        !isCategoriesPending &&
        categories.data[activeCategoryId].isSubCategoryEnabled"
      >
        <div class="category category--active" v-on:click="unSelectCategory()">
          <div class="category__name">{{categories.data[activeCategoryId].name}}</div>
        </div>

        <div class="empty-list-label" v-if="activeCategory.subCategories.length === 0">
          <div>No elements found</div>
        </div>

        <div
          v-for="subCategoryId in activeCategory.subCategories"
          :key="subCategoryId"
          class="category"
          v-on:click="selectSubCategory(subCategoryId)"
        >
          <div class="category__name">{{categories.data[subCategoryId].name}}</div>
        </div>
      </div>

      <!-- Show patches for category -->

      <div
        v-if="activeCategoryId &&
        !activeSubCategoryId &&
        !isCategoriesPending &&
        !categories.data[activeCategoryId].isTextPatchesCategory &&
        !categories.data[activeCategoryId].isSubCategoryEnabled"
      >
        <div class="category category--active" v-on:click="unSelectCategory()">
          <div class="category__name">{{categories.data[activeCategoryId].name}}</div>
        </div>

        <div v-if="isPatchesPending" class="loader">
          <b-spinner label="Loading..." variant="primary"/>
        </div>

        <div
          class="empty-list-label"
          v-if="!isPatchesPending &&
           categories.data[activeCategoryId].patchIds &&
           categories.data[activeCategoryId].patchIds.length === 0"
        >
          <div>No elements found</div>
        </div>

        <div class="patches">
          <div
            v-for="patchId in categories.data[activeCategoryId].patchIds"
            :key="patchId"
            class="patch-item"
            v-on:click="addNewPatch(patchId)"
          >
            <PatchForSideBar
              :canvasUrl="patches[patchId].fileUrl"
              :tagsMap="patches[patchId].tagsMap"
            />
            <!-- <img :src="patches[patchId].fileUrl" class="patch-item__preview"> -->
            <div class="patch-item__name">{{patches[patchId].name}}</div>
          </div>
        </div>
      </div>

      <!-- Show patches for subcategory -->

      <div v-if="activeCategoryId && activeSubCategoryId">
        <div class="category category--active2" v-on:click="unSelectCategory()">
          <div class="category__name">{{categories.data[activeCategoryId].name}}</div>
        </div>

        <div class="category category--active" v-on:click="unSelectSubCategory()">
          <div class="category__name">{{categories.data[activeSubCategoryId].name}}</div>
        </div>

        <div v-if="isPatchesPending" class="loader">
          <b-spinner label="Loading..." variant="primary"/>
        </div>

        <div
          class="empty-list-label"
          v-if="!isPatchesPending &&
           categories.data[activeSubCategoryId].isPatchesLoaded &&
           categories.data[activeSubCategoryId].patchIds.length === 0"
        >
          <div>No elements found</div>
        </div>

        <div class="patches">
          <div
            v-for="patchId in categories.data[activeSubCategoryId].patchIds"
            :key="patchId"
            class="patch-item"
            v-on:click="addNewPatch(patchId)"
          >
            <PatchForSideBar
              :canvasUrl="patches[patchId].fileUrl"
              :tagsMap="patches[patchId].tagsMap"
            />
            <!-- <img :src="patches[patchId].fileUrl" class="patch-item__preview"> -->
            <div class="patch-item__name">{{patches[patchId].name}}</div>
          </div>
        </div>
      </div>

      <!-- Show text patches-->

      <div
        v-if="activeCategoryId &&
        !isCategoriesPending &&
        categories.data[activeCategoryId].isTextPatchesCategory"
      >
        <div class="category category--active" v-on:click="unSelectCategory()">
          <div class="category__name">{{categories.data[activeCategoryId].name}}</div>
        </div>

        <div class="patches patches--text">
          <div
            v-for="fontData in fontsByCategory(categories.data[activeCategoryId].name)"
            :key="fontData.font"
            class="patch-item"
            v-on:click="addNewTextPatch(fontData)"
          >
            <div :class="fontData.font">{{fontData.title}}</div>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      id="text-patch-editor-modal"
      size="lg"
      hide-header
      hide-footer
      centered
      content-class="patch-editor-modal__content"
    >
      <TextPatchEditor
        modal-id="text-patch-editor-modal"
        :patchCategoryId="patchCategoryId"
        :size="size"
        :is-text-embroidery="isTextEmbroidery"
      />
    </b-modal>
  </div>
</template>

<script>
import svgApi from '@/api/svgApi';
import PatchEditor from '@/components/canvas/PatchEditor.vue';
import TextPatchEditor from '@/components/canvas/TextPatchEditor.vue';
import PatchForSideBar from './PatchForSidebar.vue';

export default {
  name: 'PatchSidebar',
  components: {
    TextPatchEditor,
    PatchEditor,
    PatchForSideBar
  },

  data () {
    return {
      patchSvgText: '',
      patchName: '',
      svgPatchPending: false,
      patchCategoryId: '',
      originalPatchId: '',
      size: {},
      availableSizes: ''
    };
  },

  created () {
    if (this.categories.ids.length === 0) {
      this.$store.dispatch('elements/loadPatchCategories');
    }
  },

  computed: {
    categories () {
      return this.$store.getters['elements/getCategoriesOfPatches'];
    },

    patches () {
      return this.$store.getters['elements/getPatches'];
    },

    isCategoriesPending () {
      return this.$store.getters['elements/isCategoriesPending'];
    },

    isPatchesPending () {
      return this.$store.getters['elements/isPatchesPending'];
    },

    activeCategoryId () {
      return this.$store.getters['elements/getActiveCategoryId'];
    },

    activeSubCategoryId () {
      return this.$store.getters['elements/getActiveSubCategoryId'];
    },

    activeCategory () {
      return this.categories.data[this.activeCategoryId];
    },

    fontsByCategory () {
      return (categoryName) => this.$store.getters['elements/getFontsByCategory'](categoryName);
    },

    sidebarTitle () {
      if (this.isTextEmbroidery) {
        return 'Add Text';
      }

      return 'Add Patch';
    },

    isTextEmbroidery () {
      return this.activeCategory && this.activeCategory.name === 'Text Embroidery';
    }
  },

  methods: {
    async addNewPatch (patchId) {
      this.$bvModal.show('patch-editor-modal');
      const patch = this.patches[patchId];

      this.svgPatchPending = true;
      this.patchSvgText = null;
      this.patchName = patch.name;
      this.patchCategoryId = this.activeCategoryId;
      this.availableSizes = patch.availableSlabSize;

      console.log('patch: ', patch);

      if (patch.defaultSize) {
        this.size = {
          ...this.activeCategory.sizes.find((item) => item.size === patch.defaultSize.size),
          size: patch.defaultSize.size
        };
      } else {
        this.size = {
          ...this.activeCategory.sizes
            .filter((item) => item.isChecked)[0]
        };
      }

      this.originalPatchId = patchId;

      return svgApi.loadSvg(patch.fileUrl)
        .then((svgText) => {
          this.patchSvgText = svgText;
          this.svgPatchPending = false;
          this.$store.dispatch('elements/setActivePatch', patchId);

          return true;
        });
    },

    selectCategory (categoryId) {
      const category = this.categories.data[categoryId];
      this.$store.dispatch('elements/setActiveCategory', categoryId);
      if (category.isSubCategoryEnabled || category.isTextPatchesCategory) {
        if (category.isAdditionalDataLoaded) return false;
        this.$store.dispatch('elements/loadCategory', { categoryId, orderId: this.$route.query.orderId, schoolId: this.$route.query.schoolId || null });
      } else {
        if (category.patchIds && category.patchIds.length > 0) return false;

        this.$store.dispatch('elements/loadCategory', { categoryId, orderId: this.$route.query.orderId, schoolId: this.$route.query.schoolId || null });
        this.$store.dispatch('elements/loadPatchesByCategory', { categoryId, orderId: this.$route.query.orderId || null, schoolId: this.$route.query.schoolId || null });
      }

      return true;
    },

    selectSubCategory (subCategoryId) {
      this.$store.dispatch('elements/setActiveSubCategory', subCategoryId);
      const subCategory = this.categories.data[subCategoryId];
      if (!subCategory.isPatchesLoaded) {
        this.$store.dispatch('elements/loadPatchesByCategory', { categoryId: this.activeCategoryId, orderId: this.$route.query.orderId || null, schoolId: this.$route.query.schoolId || null });
      }
    },

    unSelectCategory () {
      this.$store.dispatch('elements/setActiveCategory', null);
      this.unSelectSubCategory();
    },

    unSelectSubCategory () {
      if (this.activeSubCategoryId) {
        this.$store.dispatch('elements/setActiveSubCategory', null);
      }
    },

    addNewTextPatch (fontData) {
      this.$bvModal.show('text-patch-editor-modal');
      this.$store.dispatch('elements/setActiveFont', fontData);
      this.patchCategoryId = this.activeCategoryId;
      // eslint-disable-next-line prefer-destructuring
      const checkedSizes = this.activeCategory.sizes.filter((item) => item.isChecked);
      this.size = checkedSizes.find((item) => item.size === this.activeCategory.defaultSize);
    },

    async loadPatchSvg (fileUrl) {
      return svgApi.loadSvg(fileUrl).then((svgText) => {
        return svgText;
      });
    },
  }
};
</script>

<style scoped>
  .sidebar-content--jacket {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .loader {
    margin-top: 10px;
  }

  .categories {
    border: 1px solid #ccc;
    flex: 1;
    margin-top: 15px;
    overflow-y: auto;
    width: 100%;
  }

  .category {
    cursor: pointer;
    text-align: left;
    padding: 10px;
  }

  .category:hover,
  .category--active {
    background: #1c539c;
    color: #ffffff;
  }

  .category--active2 {
    background: #cbcaca;
    color: #ffffff;
  }

  .empty-list-label {
    margin: 10px;
    text-align: left;
  }

  .patches {
    padding-top: 1rem;
  }

  .patch-item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 200px;
    margin: 0 auto;
    margin-bottom: 1rem;

    cursor: pointer;
  }

 .patch-item__name {
   margin: 10px 0;
  }

  .patch-item__preview {
    width: 100%;
    height: 80%;
    object-fit: contain;
    margin-bottom: 10px;
  }

  .patches--text .patch-item {
    cursor: pointer;
    font-size: 30px;
    padding-left: 10px;
    text-align: left;
    width: auto;
    height: auto;
  }
</style>
